import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, mergeClasses, tokens, Button } from '@fluentui/react-components';
import { API_HOST, API_BASE } from '../../config.js';
import { iconMap } from '../../theme/icons';
import ImageLightbox from './ImageLightbox';

const ChevronLeftIcon = iconMap.chevron_left;
const ChevronRightIcon = iconMap.chevron_right;

const resolveImageUrl = (imageUrl) => (imageUrl?.startsWith('http') ? imageUrl : `${API_HOST}${imageUrl}`);

const useStyles = makeStyles({
  section: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: '3rem', paddingBottom: '3rem',
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  heading: {
    textAlign: 'center', fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXXXL,
  },
  scrollWrap: { position: 'relative' },
  scroller: {
    display: 'flex', gap: tokens.spacingHorizontalL, overflowX: 'auto',
    scrollBehavior: 'smooth', scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none', '::-webkit-scrollbar': { display: 'none' },
  },
  thumbBtn: {
    flexShrink: 0, width: '12rem', aspectRatio: '1 / 1', borderRadius: tokens.borderRadius2XLarge,
    overflow: 'hidden', backgroundColor: tokens.colorNeutralBackground3, cursor: 'zoom-in',
    scrollSnapAlign: 'start', border: 'none', padding: 0,
    '@media (min-width: 640px)': { width: '14rem' },
  },
  thumbImg: {
    width: '100%', height: '100%', objectFit: 'cover',
    transitionProperty: 'transform', transitionDuration: tokens.durationSlow,
  },
  thumbBtnHover: {
    ':hover img': { transform: 'scale(1.05)' },
  },
  // Realus Fluent <Button appearance="outline" shape="circular"> jau
  // suteikia kraštus/hover pagal Fluent2 standartą (žr. #5) — čia tik
  // plaukiojanti pozicija ir paslėpimas mobiliajame.
  navBtn: {
    display: 'none',
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    boxShadow: tokens.shadow4, backgroundColor: tokens.colorNeutralBackground1,
    '@media (min-width: 640px)': { display: 'flex' },
  },
  navBtnLeft: { left: '-1rem' },
  navBtnRight: { right: '-1rem' },
});

// typeLabel — baldo tipo pavadinimas (žr. FurnitureItem.jsx TYPE_LABELS), pagal kurį
// filtruojame galerijos nuotraukas, kad rodytume TO PATIES tipo baldų projektus.
// Kai typeLabel neduotas arba tam tipui nuotraukų nėra, rodome visą "Galerija" turinį.
const ProjectGallery = ({ typeLabel }) => {
  const s = useStyles();
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/gallery`, { signal: controller.signal })
      .then(r => r.ok ? r.json() : [])
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));
    return () => controller.abort();
  }, []);

  if (items === null) return null;

  const matching = typeLabel ? items.filter(i => i.type === typeLabel || i.title === typeLabel) : [];
  const display = matching.length > 0 ? matching : items.filter(i => i.type === 'Galerija' || !i.type);
  if (display.length === 0) return null;

  const scrollBy = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const lightboxItems = display.map(item => ({ src: resolveImageUrl(item.imageUrl), alt: item.title || t('shop.project_gallery.alt_project') }));

  return (
    <div className={s.section}>
      <h2 className={s.heading}>{t('shop.project_gallery.title')}</h2>
      <div className={s.scrollWrap}>
        <div ref={scrollRef} className={s.scroller}>
          {display.map((item, i) => (
            <button
              key={item._id}
              onClick={() => setLightboxIndex(i)}
              className={mergeClasses(s.thumbBtn, s.thumbBtnHover)}
              aria-label={t('shop.project_gallery.view_larger')}
            >
              <img
                src={resolveImageUrl(item.imageUrl)}
                alt={item.title || t('shop.project_gallery.alt_project')}
                loading="lazy"
                className={s.thumbImg}
                onError={e => { e.target.style.display = 'none'; }}
              />
            </button>
          ))}
        </div>
        {display.length > 4 && (
          <>
            <Button
              appearance="outline"
              shape="circular"
              onClick={() => scrollBy(-1)}
              aria-label={t('shop.project_gallery.scroll_left')}
              icon={<ChevronLeftIcon style={{ fontSize: 18 }} />}
              className={mergeClasses(s.navBtn, s.navBtnLeft)}
            />
            <Button
              appearance="outline"
              shape="circular"
              onClick={() => scrollBy(1)}
              aria-label={t('shop.project_gallery.scroll_right')}
              icon={<ChevronRightIcon style={{ fontSize: 18 }} />}
              className={mergeClasses(s.navBtn, s.navBtnRight)}
            />
          </>
        )}
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          items={lightboxItems}
          index={lightboxIndex}
          onNavigate={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
};

export default ProjectGallery;
